import _groupBy from 'lodash/groupBy';
import resolve from '../../../modules/api/resolve';
import { apiPost, apiStore, generateFormData } from '../../../modules/apiHelper';
import {
  getFormattedDate, invalideDate, isStaleData, now,
} from '../../../modules/dataHelper';

export default {
  async saveProgramSurvey(context, form) {
    const createProgramSurvey = async () => {
      const surveyReq = await resolve(apiPost(
        'create_prog_survey',
        generateFormData({
          name: form.name,
          progid: form.programId,
          companyid: form.companyId,
          adminid: context.rootState.User.id,
          desc: form.description || 'Program surveys',
        }),
        84,
      ));

      return surveyReq;
    };

    const updateProgramSurvey = async () => {
      const surveyReq = await resolve(apiPost(
        'prog_edit_suv_name',
        generateFormData({
          suvid: form.id,
          name: form.name,
        }),
        84,
      ));

      return surveyReq;
    };

    let result;

    if (form.id) {
      // updating
      result = await updateProgramSurvey();
    } else {
      result = await createProgramSurvey();
    }

    return result;
  },

  async saveProgramSurveyQuestion(context, form) {
    const createProgramSurveyQuestion = async () => {
      const questionReq = await resolve(apiPost(
        'prog_create_suv_q',
        generateFormData({
          companyid: form.companyId,
          progid: form.programId,
          typeid: form.surveyTypeId,
          ratings: '1', // not used
          question: form.question,
          q_type: form.questionTypeId,
          start: getFormattedDate(new Date()),
          duration: '7', // days
        }),
        84,
      ));

      return questionReq;
    };

    const updateProgramSurveyQuestion = async () => {
      const surveyReq = await resolve(apiPost(
        'prog_edit_suv_q',
        generateFormData({
          qid: form.id,
          question: form.question,
        }),
        84,
      ));

      return surveyReq;
    };

    let result;

    if (form.id) {
      // updating
      result = await updateProgramSurveyQuestion();
    } else {
      result = await createProgramSurveyQuestion();
    }

    return result;
  },

  async saveProgramSurveyQuestionRating(context, form) {
    const createProgramSurveyQuestionRating = async () => {
      const ratingReq = await resolve(apiPost(
        'create_prog_survey_rating',
        generateFormData({
          companyid: form.companyId,
          progid: form.programId,
          qid: form.questionId,
          ratings: form.rateName,
        }),
        84,
      ));

      return ratingReq;
    };

    const updateProgramSurveyQuestionRating = async () => {
      const surveyReq = await resolve(apiPost(
        'prog_edit_suv_r',
        generateFormData({
          rid: form.id,
          ratings: form.rateName,
        }),
        84,
      ));

      return surveyReq;
    };

    let result;

    if (form.id) {
      // updating
      result = await updateProgramSurveyQuestionRating();
    } else {
      result = await createProgramSurveyQuestionRating();
    }

    return result;
  },

  async getCompanyProgramSurveys(context, [CompanyProgramId, refresh = false]) {
    const programid = CompanyProgramId || context.rootState.User.companyprogramid;
    if (!programid) return false;

    const surveys = await apiStore.getItem(`surveys/${programid}`) || [invalideDate, []];

    if (!refresh && !isStaleData(surveys[0]) && surveys[1].length) return surveys[1];

    const result = await resolve(apiPost('prog_suv_log', generateFormData({ progid: programid }), 84))
      .then((data) => {
        if (!data) return false;

        const programSurveys = data.prog_suv_log || [];

        if (programSurveys.length) {
          apiStore.setItem(`surveys/${programid}`, [now(), programSurveys]);
        }

        return programSurveys;
      });
    //

    return result;
  },

  async getProgramSurveyTypes(context, [CompanyProgramId, refresh = false]) {
    const programid = CompanyProgramId || context.rootState.User.companyprogramid;
    if (!programid) return false;

    const surveyTypes = await apiStore.getItem(`surveyTypes/${programid}`) || [invalideDate, []];

    if (!refresh && !isStaleData(surveyTypes[0]) && surveyTypes[1].length) return surveyTypes[1];

    const result = await resolve(apiPost('prog_suv_type_log', generateFormData({ progid: programid }), 84))
      .then((data) => {
        if (!data) return false;

        return data.prog_suv_type_log || [];
      });
    //

    return result;
  },

  async getProgramSurveyQuestionTypes(context, [CompanyProgramId, refresh = false]) {
    const programid = CompanyProgramId || context.rootState.User.companyprogramid;
    if (!programid) return false;

    const surveyQuestionTypes = await apiStore.getItem(`surveyQuestionTypes/${programid}`) || [invalideDate, []];

    if (!refresh && !isStaleData(surveyQuestionTypes[0]) && surveyQuestionTypes[1].length) return surveyQuestionTypes[1];

    const result = await resolve(apiPost('prog_suv_q_type_log', generateFormData({ progid: programid }), 84))
      .then((data) => {
        if (!data) return false;

        const log = data.prog_suv_q_type_log || [];

        if (log.length) {
          apiStore.setItem(`surveyQuestionTypes/${programid}`, [now(), log]);
        }

        return log;
      });
    //

    return result;
  },

  async getProgramSurveyQuestions(context, [CompanyProgramId, refresh = false, groupByType = true]) {
    const programid = CompanyProgramId || context.rootState.User.companyprogramid;
    if (!programid) return false;

    const surveyQuestions = await apiStore.getItem(`surveyQuestions/${programid}`) || [invalideDate, []];

    if (!refresh && !isStaleData(surveyQuestions[0]) && surveyQuestions[1].length) return surveyQuestions[1];

    const surveyTypes = await context.dispatch('getProgramSurveyTypes', [programid, refresh]);
    const surveyQuestionTypes = await context.dispatch('getProgramSurveyQuestionTypes', [programid, refresh]);

    const result = await Promise.all([
      resolve(apiPost('prog_suv_q_log', generateFormData({ progid: programid }), 84)), // active questions
      resolve(apiPost('prog_suv_q_dev_log', generateFormData({ progid: programid }), 84)), // deactivated questions
    ])
      .then(async ([activeQuestions, deactivatedQuestions]) => {
        if (!activeQuestions || !deactivatedQuestions) return false;

        // Get ratings for each question
        const ratingsPromises = [];
        [
          ...(activeQuestions.prog_suv_q_log || []),
          ...(deactivatedQuestions.prog_suv_q_dev_log || []),
        ].forEach((question) => {
          const surveyType = (surveyTypes || []).find((type) => Number(type.id) === Number(question.typeid));
          const surveyQuestionType = (surveyQuestionTypes || []).find((type) => Number(type.id) === Number(question.q_type));

          ratingsPromises.push(
            context.dispatch('getProgramSurveyQuestionRatings', [programid, question.id, refresh])
              .then((ratings) => ({
                ...question,
                type: surveyQuestionType,
                surveyType,
                ratings: ratings || [],
              })),
          );
        });

        const surveyQuestionsWithRatings = await Promise.all(ratingsPromises);

        return groupByType ? _groupBy(surveyQuestionsWithRatings, 'typeid') : surveyQuestionsWithRatings;
      });
    //

    return result;
  },

  async getProgramSurveyQuestionRatings(context, [CompanyProgramId, questionId, refresh = false]) {
    const programid = CompanyProgramId || context.rootState.User.companyprogramid;
    if (!programid) return false;

    const questionRatings = await apiStore.getItem(`questionRatings/${questionId}`) || [invalideDate, []];

    if (!refresh && !isStaleData(questionRatings[0]) && questionRatings[1].length) return questionRatings[1];

    const result = await resolve(apiPost('prog_suv_ratings_log', generateFormData({ qid: questionId }), 84))
      .then((data) => {
        if (!data) return false;

        const programSurveyQuestionRatings = (data.prog_suv_ratings_log || []);

        if (programSurveyQuestionRatings.length) {
          apiStore.setItem(`questionRatings/${questionId}`, [now(), programSurveyQuestionRatings]);
        }

        return programSurveyQuestionRatings;
      });
    //

    return result;
  },

  async getCompanyProgramSurveyResponses(context, [CompanyProgramId, questionTypeId]) {
    const programid = CompanyProgramId || context.rootState.User.companyprogramid;
    if (!programid) return false;

    const surveyQuestions = await context.dispatch('getProgramSurveyQuestions', [programid, true, true]);
    const wantedQuestions = surveyQuestions?.[questionTypeId];

    const surveyResponses = await resolve(apiPost('prog_user_rsp_log', generateFormData({ progid: programid, typeid: questionTypeId }), 84))
      .then((data) => {
        if (!data) return false;

        return data.prog_user_rsp_log || [];
      });
    //

    if (!surveyResponses || !surveyQuestions) {
      return false;
    }

    // get unique users details
    const uniqueUsersIds = [...new Set(surveyResponses.map((response) => response.userid))];
    const uniqueUsers = await Promise.all(uniqueUsersIds.map((userId) => context.dispatch('getUserData', userId)));

    const promises = [];

    const mappedQuestions = await Promise.all(
      wantedQuestions.map(async (question) => {
        // filter responses that are only for the question
        const responses = surveyResponses.filter((response) => Number(question.id) === Number(response.qid));

        // get user of each response
        const mappedResponses = await Promise.all(
          responses.map(async (response) => {
            const userResponded = uniqueUsers.find((user) => Number(user.id) === Number(response.userid));

            return {
              ...response,
              user: userResponded,
            };
          }),
        );

        return {
          ...question,
          responses: mappedResponses,
        };
      }),
    );

    await Promise.all(promises);

    // console.log(mappedQuestions);

    return mappedQuestions;
  },

  async activateProgramSurvey(context, surveyId) {
    const result = await resolve(apiPost(
      'activate_survey_pillar',
      generateFormData({
        pillarid: surveyId,
        start: '11-12-2024',
        end: '15-12-2024',
      }),
      34,
    ));

    return result;
  },

  async deactivateProgramSurvey(context, surveyId) {
    const result = await resolve(apiPost(
      'dev_suv_pillar',
      generateFormData({
        pillarid: surveyId,
        reason: 'not relevant',
      }),
      33,
    ));

    return result;
  },

  async activateProgramSurveyQuestion(context, surveyQuestionId) {
    const result = await resolve(apiPost(
      'prog_suv_q_act',
      generateFormData({
        qid: surveyQuestionId,
      }),
      84,
    ));

    return result;
  },

  async deactivateProgramSurveyQuestion(context, surveyQuestionId) {
    const result = await resolve(apiPost(
      'prog_suv_q_dev',
      generateFormData({
        qid: surveyQuestionId,
        reason: 'not relevant',
      }),
      84,
    ));

    return result;
  },

  async createProgramSurveyType(context, form) {
    const result = await resolve(apiPost(
      'create_prog_survey_type',
      generateFormData(form),
      84,
    ));

    return result;
  },
};
